import React, { useEffect, useState, createContext, useContext } from 'react';
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}
interface User {
  email: string;
  uid: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  role?: string;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Demo mode - always allow access
const CURRENT_USER_KEY = 'demo_current_user';
const getCurrentUser = (): User | null => {
  const stored = localStorage.getItem(CURRENT_USER_KEY);
  return stored ? JSON.parse(stored) : null;
};
const setCurrentUser = (user: User | null) => {
  if (user) {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(CURRENT_USER_KEY);
  }
};
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Check for existing session on mount
    const currentUser = getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);
  const login = async (email: string, password: string) => {
    // Simulate network delay for realism
    await new Promise(resolve => setTimeout(resolve, 800));
    // Demo mode: Always succeed with any credentials
    const demoUser: User = {
      email,
      uid: `demo_${Date.now()}`,
      firstName: 'Demo',
      lastName: 'User'
    };
    setUser(demoUser);
    setCurrentUser(demoUser);
  };
  const logout = async () => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    setUser(null);
    setCurrentUser(null);
  };
  const signUp = async (email: string, password: string) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));
    // Demo mode: Always succeed
    const demoUser: User = {
      email,
      uid: `demo_${Date.now()}`
    };
    setUser(demoUser);
    setCurrentUser(demoUser);
  };
  const resetPassword = async (email: string) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    // Demo mode: Always succeed
    console.log(`Demo: Password reset for ${email}`);
  };
  const value = {
    isAuthenticated: !!user,
    user,
    login,
    logout,
    signUp,
    resetPassword
  };
  return <AuthContext.Provider value={value} data-id="element-27">
      {!loading && children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}